/**
 * Add Slides Tool
 *
 * Simple tool for adding slides via COM backend:
 * - addEmpty: insert blank slide with layout
 * - copySlide: copy existing slide (optionally clearing content)
 */

import { ToolBase } from "../../../base/toolBase";
import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { backendClient } from "../../../../../services/communication/backendClient";
import { getOrCreatedocumentId } from "../../../../../services/documentIdManager";
import {
    AddSlidesParamsSchema,
    type AddSlidesParams,
    type AddSlidesResult,
    type OperationResult,
} from "./addSlides.schema";

// =============================================================================
// TOOL DESCRIPTION
// =============================================================================

const SYSTEM_PROMPT_FALLBACK = `### addSlides
Create new slides in the active PowerPoint presentation using fast COM operations.

**When to use:**
- User asks to add/insert/create a new slide
- You need a blank slide with a specific layout
- You need to duplicate a slide (optionally clearing content)

**Operation types:**
- addEmpty: Insert a blank slide with a chosen layout
- copySlide: Copy an existing slide (optional clearOptions)

**Positions are 1-based** (position 1 = before slide 1).

**Layout selection (addEmpty) — choose ONE approach:**
- layoutId (+ optional layoutName) from "PRESENTATION STYLE OVERVIEW"
- sourceSlideRef to copy layout from an existing slide (1-based index or slide_N ID)

**copySlide source:**
- sourceSlideId can be 1-based index or slide_N ID
- clearOptions can remove text/tables/charts while keeping layout

**Example: add empty title slide**
\`\`\`json
{
  "operations": [
    {
      "addEmpty": {
        "position": 1,
        "layoutId": "layout_2147483951#0",
        "layoutName": "Title Slide"
      }
    }
  ]
}
\`\`\`

**Example: copy slide 5 and clear text**
\`\`\`json
{
  "operations": [
    {
      "copySlide": {
        "position": 8,
        "sourceSlideId": 5,
        "clearOptions": {
          "clearTextContent": true,
          "resetPlaceholders": false,
          "clearTableContent": false,
          "clearChartData": false,
          "removeNonPlaceholderShapes": false
        }
      }
    }
  ]
}
\`\`\``;

const TOOL_DESCRIPTION = `Add new slides to the active presentation using COM operations.
Supports:
- addEmpty (insert blank slide with layout)
- copySlide (copy an existing slide, optional content clearing)

Positions are 1-based.`;

// =============================================================================
// TOOL IMPLEMENTATION
// =============================================================================

export class AddSlidesTool extends ToolBase<AddSlidesParams, AddSlidesResult> {
    public readonly name = "addSlides";
    public readonly description: ToolDescription;
    public readonly systemPromptDescription = SYSTEM_PROMPT_FALLBACK;
    fieldsToCheck = [
        { name: "uiDescription", type: "string" as const, list_status: false, order: 0 },
    ];

    constructor() {
        super();
        this.description = new ToolDescription(
            this.name,
            TOOL_DESCRIPTION,
            AddSlidesParamsSchema,
        );
    }

    async execute(params: unknown): Promise<AddSlidesResult> {
        const parsed = safeParseToolParams(AddSlidesParamsSchema, params);
        if (!parsed.success) {
            return {
                success: false,
                message: `Invalid parameters: ${parsed.error}`,
                operations: [],
                slidesCreated: 0,
                metadata: {
                    totalTime: 0,
                    slidesAdded: 0,
                    slidesCopied: 0,
                },
            };
        }

        const startTime = Date.now();
        const documentId = await getOrCreatedocumentId();
        const operations: OperationResult[] = [];

        for (const op of parsed.data.operations) {
            if ("addEmpty" in op) {
                const internalPosition = op.addEmpty.position - 1;
                console.log(
                    `[AddSlidesTool] Executing addEmpty at position ${op.addEmpty.position} (1-based) → ${internalPosition} (0-based)` +
                    `${op.addEmpty.layoutName ? ` with layout "${op.addEmpty.layoutName}"` : ""}`
                );

                try {
                    const result = await backendClient.comAddEmptySlide({
                        documentId,
                        position: internalPosition,
                        layoutId: op.addEmpty.layoutId,
                        layoutName: op.addEmpty.layoutName,
                        sourceSlideRef: op.addEmpty.sourceSlideRef?.toString(),
                    });

                    operations.push({
                        operationType: "addEmpty",
                        position: op.addEmpty.position,
                        slideId: result.slideId || `slide_${op.addEmpty.position}`,
                        status: result.success ? "completed" : "failed",
                        error: result.error,
                    });
                } catch (error) {
                    console.warn("[AddSlidesTool] addEmpty failed:", error);
                    operations.push({
                        operationType: "addEmpty",
                        position: op.addEmpty.position,
                        slideId: "",
                        status: "failed",
                        error: error instanceof Error ? error.message : String(error),
                    });
                }
            }

            if ("copySlide" in op) {
                const internalPosition = op.copySlide.position - 1;
                console.log(
                    `[AddSlidesTool] Executing copySlide from ${op.copySlide.sourceSlideId} to position ${op.copySlide.position} (1-based) → ${internalPosition} (0-based)` +
                    `${op.copySlide.clearOptions ? " (with clear options)" : ""}`
                );

                try {
                    const result = await backendClient.comCopySlide({
                        documentId,
                        position: internalPosition,
                        sourceSlideId: String(op.copySlide.sourceSlideId),
                        clearOptions: op.copySlide.clearOptions,
                    });

                    operations.push({
                        operationType: "copySlide",
                        position: op.copySlide.position,
                        slideId: result.slideId || `slide_${op.copySlide.position}`,
                        status: result.success ? "completed" : "failed",
                        error: result.error,
                    });
                } catch (error) {
                    console.warn("[AddSlidesTool] copySlide failed:", error);
                    operations.push({
                        operationType: "copySlide",
                        position: op.copySlide.position,
                        slideId: "",
                        status: "failed",
                        error: error instanceof Error ? error.message : String(error),
                    });
                }
            }
        }

        const slidesAdded = operations.filter(
            (op) => op.operationType === "addEmpty" && op.status === "completed",
        ).length;
        const slidesCopied = operations.filter(
            (op) => op.operationType === "copySlide" && op.status === "completed",
        ).length;
        const slidesCreated = slidesAdded + slidesCopied;
        const totalTime = Date.now() - startTime;
        const success = operations.every((op) => op.status === "completed");

        return {
            success,
            slidesCreated,
            operations,
            metadata: {
                totalTime,
                slidesAdded,
                slidesCopied,
            },
        };
    }
}
